// BRE gift card contract (docs/contracts/pos-gift-cards.md).
//
// Source of truth today: pos `gift_cards` / `gift_card_ledger`. pos exposes no
// reserve/commit/release/refund API to other services yet; these schemas are
// the PROPOSED internal endpoints pos would add, authenticated with a new
// purpose-scoped TICKETING_GIFT_CARD_SECRET. A gift card is a TENDER in
// Ticketing, never a discount: it relieves gift-card liability and leaves
// ticket revenue untouched.
import { z } from 'zod';
import { Id, IdempotencyKey, NonNegativeCents, PositiveCents, Timestamp } from '../primitives';

const ExternalRef = z.object({
  source_service: z.literal('bre-ticketing'),
  order_id: Id,
  payment_id: Id,
});

/**
 * POST /api/internal/gift-cards/issue — make a new card and put value on it.
 *
 * Ticketing needs this to pay a flexible-ticket refund back as credit (D-191).
 * pos owns gift cards, and today it can only be asked about cards that already
 * exist; this is the one endpoint the feature waits on.
 *
 * **Idempotent on `idempotency_key`, and that is the whole contract.** Issuing
 * twice is minting money, so a repeat must return the same card rather than a
 * second one, forever, not for some window.
 */
export const GiftCardIssueRequest = z.object({
  amount_cents: PositiveCents,
  /** The BRE-Auth subject the card belongs to, when the buyer has an account. */
  customer_sub: Id.nullish(),
  /** Why it exists, for pos's own ledger and for anybody reading it later. */
  reason: z.enum(['TICKETING_FLEXIBLE_REFUND']),
  external_ref: z.object({ source_service: z.literal('bre-ticketing'), order_id: Id, refund_id: Id }),
  idempotency_key: IdempotencyKey,
});
export const GiftCardIssueResponse = z.object({
  gift_card_ref: z.string(),
  /** What the customer types or scans to spend it. */
  /** Eight unambiguous characters a person can read out (D-209). */
  short_code: z.string().optional(),
  credential: z.string(),
  masked_identifier: z.string(),
  balance_cents: NonNegativeCents,
  already_issued: z.boolean(),
});

/** POST /api/internal/gift-cards/resolve — scanned token or typed short code. */
export const GiftCardResolveRequest = z.object({ credential: z.string().min(8).max(512) });
export const GiftCardResolveResponse = z.object({
  gift_card_ref: z.string(),
  masked_identifier: z.string(),
  status: z.enum(['active', 'unactivated', 'voided']),
  available_cents: NonNegativeCents,
});

/** POST /api/internal/gift-cards/reserve — moves balance into a hold. */
export const GiftCardReserveRequest = z.object({
  gift_card_ref: z.string(),
  amount_cents: PositiveCents,
  ttl_seconds: z.int().min(60).max(900),
  external_ref: ExternalRef,
  idempotency_key: IdempotencyKey,
});
export const GiftCardReserveResponse = z.object({
  reservation_id: z.string(),
  reserved_cents: PositiveCents,
  expires_at: Timestamp,
  available_after_cents: NonNegativeCents,
});

/** POST /api/internal/gift-cards/reservations/:id/commit */
export const GiftCardCommitRequest = z.object({ idempotency_key: IdempotencyKey });
export const GiftCardCommitResponse = z.object({
  reservation_id: z.string(),
  committed_cents: PositiveCents,
  ledger_ref: z.string(),
  already_committed: z.boolean(),
});

/** POST /api/internal/gift-cards/reservations/:id/release — always safe to repeat. */
export const GiftCardReleaseRequest = z.object({ idempotency_key: IdempotencyKey });
export const GiftCardReleaseResponse = z.object({ reservation_id: z.string(), released: z.boolean() });

/**
 * POST /api/gift-cards/internal/void — take back a card this service minted.
 *
 * `voided_cents` is **reported, not requested**: only pos knows what is still
 * on the card, and a card issued for $49 and spent to $29 at the bar can give
 * back $29. The caller reverses exactly this and no more.
 */
export const GiftCardVoidResponse = z.object({
  gift_card_ref: z.string(),
  voided_cents: NonNegativeCents,
  already_voided: z.boolean(),
});

/** POST /api/internal/gift-cards/reservations/:id/refund — restore to the same card. */
export const GiftCardRefundRequest = z.object({
  amount_cents: PositiveCents,
  external_refund_ref: z.object({
    source_service: z.literal('bre-ticketing'),
    refund_id: Id,
    refund_payment_id: Id,
  }),
  idempotency_key: IdempotencyKey,
});
export const GiftCardRefundResponse = z.object({
  restored_cents: PositiveCents,
  ledger_ref: z.string(),
  already_applied: z.boolean(),
});

/** GET /api/internal/gift-cards/redemptions?source_service=bre-ticketing&from=&to= (reconciliation). */
export const GiftCardRedemptionsResponse = z.object({
  redemptions: z.array(
    z.object({
      ledger_ref: z.string(),
      reservation_id: z.string(),
      kind: z.enum(['redeem', 'refund_restore']),
      amount_cents: PositiveCents,
      external_ref: z.record(z.string(), z.string()),
      occurred_at: Timestamp,
    }),
  ),
});
